import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { portal as portalApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDate, formatTime, isSecondTuesday } from '../utils/portal';

/**
 * Every committee meeting, newest first — the archive of agendas and minutes.
 */
export default function PortalMeetingsPage() {
  const { isAdmin, isChair } = useAuth();
  const navigate = useNavigate();
  const canManage = isAdmin || isChair;

  const [meetings, setMeetings] = useState([]);
  const [nextDate, setNextDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ meeting_date: '', title: '', start_time: '', location: '', exception_note: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [list, overview] = await Promise.all([portalApi.listMeetings(), portalApi.getOverview()]);
      setMeetings(list);
      setNextDate(overview.next_scheduled_date);
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openForm = () => {
    setForm({ meeting_date: nextDate, title: '', start_time: '', location: '', exception_note: '' });
    setShowForm(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const meeting = await portalApi.createMeeting(form);
      navigate(`/volunteer/meetings/${meeting.id}`);
    } catch (err) {
      setMessage(`Error: ${err.message}`);
      setSaving(false);
    }
  };

  // Anything other than the 2nd Tuesday needs a word of explanation.
  const dateIsOffCycle = form.meeting_date && !isSecondTuesday(form.meeting_date);

  if (loading) return <div className="loading">Loading meetings…</div>;

  return (
    <div className="portal-page">
      <div className="portal-header">
        <div>
          <h1>Meetings</h1>
          <p className="portal-subtitle">
            <Link to="/volunteer">← Volunteers</Link> · Standing cadence: 2nd Tuesday of each month
          </p>
        </div>
        {canManage && (
          <button className="btn-sm btn-primary" onClick={openForm}>Schedule a Meeting</button>
        )}
      </div>

      {message && (
        <div className={`alert ${message.startsWith('Error') ? 'alert-error' : 'alert-success'}`}>
          {message}<button onClick={() => setMessage(null)}>✕</button>
        </div>
      )}

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <form className="portal-action-form" onSubmit={handleCreate}>
              <h3>Schedule a Meeting</h3>

              <label>
                Date
                <input
                  type="date"
                  value={form.meeting_date}
                  onChange={(e) => setForm({ ...form, meeting_date: e.target.value })}
                  required
                />
              </label>
              <p className="field-hint">
                Next 2nd Tuesday is <strong>{formatDate(nextDate)}</strong>.
                {dateIsOffCycle && ' A different date is fine — say why below.'}
              </p>

              <div className="portal-form-row">
                <label>
                  Start time <span className="field-hint">(optional)</span>
                  <input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
                </label>
                <label>
                  Location <span className="field-hint">(optional)</span>
                  <input type="text" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
                </label>
              </div>

              <label>
                Title <span className="field-hint">(defaults to "LCPOA Trails - Mon Year Meeting")</span>
                <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </label>

              <label>
                Why off-cycle? <span className="field-hint">(only needed if this isn't the 2nd Tuesday)</span>
                <input
                  type="text"
                  value={form.exception_note}
                  onChange={(e) => setForm({ ...form, exception_note: e.target.value })}
                  placeholder="e.g. moved a week for the holiday"
                />
              </label>

              <div className="portal-form-actions">
                <button type="button" className="btn-sm btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn-sm btn-primary" disabled={saving}>
                  {saving ? 'Scheduling…' : 'Schedule'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {meetings.length === 0 ? (
        <div className="portal-empty">
          <p>No meetings yet. The next 2nd Tuesday is <strong>{formatDate(nextDate)}</strong>.</p>
        </div>
      ) : (
        <div className="portal-meeting-list">
          {meetings.map((m) => (
            <Link to={`/volunteer/meetings/${m.id}`} className="portal-meeting-row" key={m.id}>
              <div className="portal-meeting-row-date">
                <span className="portal-meeting-row-month">
                  {formatDate(m.meeting_date, { month: 'short' })}
                </span>
                <span className="portal-meeting-row-day">
                  {formatDate(m.meeting_date, { day: 'numeric' })}
                </span>
                <span className="portal-meeting-row-year">
                  {formatDate(m.meeting_date, { year: 'numeric' })}
                </span>
              </div>

              <div className="portal-meeting-row-body">
                <div className="portal-meeting-row-title">{m.title}</div>
                <div className="portal-meeting-row-meta">
                  {m.start_time && <span>{formatTime(m.start_time)}</span>}
                  {m.location && <span>{m.location}</span>}
                  {m.action_count > 0 && (
                    <span>{m.open_action_count} of {m.action_count} action items open</span>
                  )}
                </div>
              </div>

              <div className="portal-meeting-row-badges">
                {m.status === 'cancelled' && <span className="portal-badge badge-cancelled">Cancelled</span>}
                {m.is_exception && <span className="portal-badge badge-exception">Off-cycle</span>}
                {m.agenda_md && <span className="portal-badge badge-agenda">Agenda</span>}
                {m.minutes_published_at
                  ? <span className="portal-badge badge-published">Minutes</span>
                  : m.minutes_md && <span className="portal-badge badge-draft">Minutes draft</span>}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
